import { apiRequest } from './authService';

/**
 * Loans service — mirrors walletService.
 * Balance semantics: positive balance = amount the client OWES (outstanding loan).
 * disbursement increases the debt, repayment decreases it.
 */

/**
 * Get all loan balances for a customer.
 * @param {string|number} customerId
 * @returns {Promise<{success: boolean, data?: array, message?: string}>}
 */
export async function getCustomerLoans(customerId) {
  try {
    const response = await apiRequest('loans/customer', {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId })
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to fetch loans' };
  } catch (error) {
    console.error('Get customer loans error:', error);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

/**
 * Disburse a loan to a customer (increases outstanding balance).
 * @param {string|number} customerId
 * @param {string} currency USD, EUR, SRD
 * @param {number} amount positive
 * @param {string} description
 */
export async function disbursement(customerId, currency, amount, description = 'Disbursement') {
  try {
    const response = await apiRequest('loans/disbursement', {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId,
        currency: currency,
        amount: Math.abs(amount),
        description: description
      })
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to disburse' };
  } catch (error) {
    console.error('Disbursement error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Record a repayment (decreases outstanding balance).
 * @param {string|number} customerId
 * @param {string} currency USD, EUR, SRD
 * @param {number} amount positive (API makes it negative)
 * @param {string} description
 */
export async function repayment(customerId, currency, amount, description = 'Repayment') {
  try {
    const response = await apiRequest('loans/repayment', {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId,
        currency: currency,
        amount: Math.abs(amount),
        description: description
      })
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to record repayment' };
  } catch (error) {
    console.error('Repayment error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Initialize loan balances for a customer (all three currencies).
 * @param {string|number} customerId
 */
export async function initializeLoans(customerId) {
  try {
    const response = await apiRequest('loans/initialize', {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId })
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to initialize loans' };
  } catch (error) {
    console.error('Initialize loans error:', error);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

/**
 * Search loan transactions for a customer.
 */
export async function searchTransactions(customerId, searchQuery, currency = null, limit = 100) {
  try {
    const body = { customer_id: customerId, query: searchQuery, limit: limit };
    if (currency) body.currency = currency;

    const response = await apiRequest('loan-transactions/search', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to search transactions' };
  } catch (error) {
    console.error('Search loan transactions error:', error);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

/**
 * Get loan transaction history for a customer.
 */
export async function getCustomerTransactions(customerId, currency = null, limit = 100) {
  try {
    const body = { customer_id: customerId, limit: limit };
    if (currency) body.currency = currency;

    const response = await apiRequest('loan-transactions/customer', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to fetch transactions' };
  } catch (error) {
    console.error('Get loan transactions error:', error);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

/**
 * Delete a loan transaction and reverse its effect on the loan balance.
 */
export async function deleteTransaction(transactionId) {
  try {
    const response = await apiRequest('loan-transactions/delete', {
      method: 'POST',
      body: JSON.stringify({ transaction_id: transactionId })
    });

    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to delete transaction' };
  } catch (error) {
    console.error('Delete loan transaction error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Edit a loan transaction (amount, type, description). created_at preserved.
 * @param {string|number} transactionId
 * @param {{amount: number, type: string, description?: string}} payload
 */
export async function editTransaction(transactionId, payload) {
  try {
    const body = {
      transaction_id: transactionId,
      amount: payload.amount,
      type: payload.type,
      description: payload.description || ''
    };

    const response = await apiRequest('loan-transactions/edit', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to edit transaction' };
  } catch (error) {
    console.error('Edit loan transaction error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Fetch the edit audit log for a single loan transaction.
 */
export async function getTransactionEdits(transactionId) {
  try {
    const response = await apiRequest('loan-transactions/edits', {
      method: 'POST',
      body: JSON.stringify({ transaction_id: transactionId })
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to fetch edit history' };
  } catch (error) {
    console.error('Get loan transaction edits error:', error);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

/**
 * Get global loan transaction history with optional filters.
 */
export async function getTransactionsHistory(filters = {}) {
  try {
    const body = {};
    if (filters.date_from !== undefined && filters.date_from !== null && filters.date_from !== '') {
      body.date_from = filters.date_from;
    }
    if (filters.date_to !== undefined && filters.date_to !== null && filters.date_to !== '') {
      body.date_to = filters.date_to;
    }
    if (filters.customer_id) body.customer_id = filters.customer_id;
    if (filters.currency) body.currency = filters.currency;
    if (filters.type) body.type = filters.type;
    if (filters.limit) body.limit = filters.limit;

    const response = await apiRequest('loan-transactions/history', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to fetch loan transaction history' };
  } catch (error) {
    console.error('Get loan transactions history error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Get full loan transaction history for a customer for PDF export.
 */
export async function getTransactionsExport(customerId, currency = null) {
  try {
    const body = { customer_id: customerId };
    if (currency) body.currency = currency;

    const response = await apiRequest('loan-transactions/export', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to fetch export data' };
  } catch (error) {
    console.error('Get loan transactions export error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Get total outstanding loan balances for all clients.
 * @returns {Promise<{success: boolean, data?: {USD, EUR, SRD}, message?: string}>}
 */
export async function getAllClientsBalance() {
  try {
    const response = await apiRequest('loans/all-clients-balance', { method: 'POST' });

    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return {
      success: false,
      data: { USD: 0, EUR: 0, SRD: 0 },
      message: response.response || 'Failed to fetch total loan balances'
    };
  } catch (error) {
    console.error('Get all clients loan balance error:', error);
    return {
      success: false,
      data: { USD: 0, EUR: 0, SRD: 0 },
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Helper: get loan balances as an object keyed by currency.
 * @param {string|number} customerId
 * @returns {Promise<{USD: number, EUR: number, SRD: number}>}
 */
export async function getLoanBalances(customerId) {
  const result = await getCustomerLoans(customerId);

  const balances = { USD: 0, EUR: 0, SRD: 0 };

  if (result.success && result.data) {
    result.data.forEach(loan => {
      balances[loan.currency] = parseFloat(loan.amount) || 0;
    });
  }

  return balances;
}
